import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

const RiskDisclosure = () => {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      padding: '40px 24px'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Back Link */}
        <Link to="/" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          color: 'var(--accent-gold)',
          textDecoration: 'none',
          marginBottom: '32px',
          fontSize: '14px'
        }}>
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '40px'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            background: 'rgba(239, 68, 68, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AlertTriangle size={28} color="var(--error)" />
          </div>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: '4px'
            }}>Risk Disclosure</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
              Last updated: January 2026
            </p>
          </div>
        </div>

        {/* Warning Banner */}
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px'
        }}>
          <AlertTriangle size={24} color="var(--error)" style={{ flexShrink: 0, marginTop: '2px' }} />
          <p style={{ color: 'var(--error)', fontSize: '14px', lineHeight: 1.6 }}>
            <strong>IMPORTANT:</strong> Trading synthetic indices and other financial instruments involves substantial risk of loss and is not suitable for all investors. Please read this entire disclosure carefully before using our services.
          </p>
        </div>

        {/* Content */}
        <div style={{
          background: 'var(--bg-secondary)',
          borderRadius: '16px',
          border: '1px solid var(--border-subtle)',
          padding: '40px'
        }}>
          <div style={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: '15px' }}>
            <section style={{ marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>
                1. General Risk Warning
              </h2>
              <p style={{ marginBottom: '12px' }}>
                Trading in synthetic indices and other financial instruments carries a high level of risk and may not be suitable for all investors. Before deciding to trade, you should carefully consider your investment objectives, level of experience, and risk appetite.
              </p>
              <p>
                The possibility exists that you could sustain a loss of some or all of your initial investment. You should not invest money that you cannot afford to lose.
              </p>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>
                2. Synthetic Indices Specific Risks
              </h2>
              <ul style={{ marginLeft: '24px' }}>
                <li style={{ marginBottom: '12px' }}>
                  <strong>Market Volatility:</strong> Synthetic indices are designed to simulate real market conditions with varying levels of volatility. High volatility can lead to rapid and significant losses.
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong>24/7 Trading:</strong> While synthetic indices offer continuous trading, this means exposure to market movements at all times, including when you may not be actively monitoring your positions.
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong>Leverage Risk:</strong> Trading with leverage amplifies both potential profits and potential losses. A small market movement can have a proportionally larger impact on your account.
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong>Technical Risks:</strong> System failures, connectivity issues, or platform errors can affect trade execution and may result in losses.
                </li>
              </ul>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>
                3. Prop Trading Specific Risks
              </h2>
              <ul style={{ marginLeft: '24px' }}>
                <li style={{ marginBottom: '12px' }}>
                  <strong>Account Termination:</strong> Failing to meet trading rules (daily drawdown, maximum drawdown, profit targets) will result in immediate account termination without refund of the initial fee.
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong>No Guaranteed Returns:</strong> Past performance is not indicative of future results. There is no guarantee of profitability.
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong>Evaluation Failure:</strong> For 1-Step accounts, there is a risk of failing the evaluation phase and losing the account fee.
                </li>
              </ul>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>
                4. Financial Risks
              </h2>
              <ul style={{ marginLeft: '24px' }}>
                <li style={{ marginBottom: '12px' }}>
                  <strong>Loss of Capital:</strong> You may lose your entire investment (account fee) if you breach the trading rules.
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong>Cryptocurrency Payment Risks:</strong> Cryptocurrency transactions are irreversible. Exchange rate fluctuations may affect the value of your payments.
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong>Payout Delays:</strong> While we strive for quick payouts, processing times may vary due to verification requirements or technical issues.
                </li>
              </ul>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>
                5. Acknowledgment
              </h2>
              <p style={{ marginBottom: '12px' }}>By using PROP INDEXA's services, you acknowledge that:</p>
              <ul style={{ marginLeft: '24px' }}>
                <li style={{ marginBottom: '8px' }}>You have read and understood this Risk Disclosure</li>
                <li style={{ marginBottom: '8px' }}>You are aware of the risks involved in trading</li>
                <li style={{ marginBottom: '8px' }}>You are trading with funds you can afford to lose</li>
                <li style={{ marginBottom: '8px' }}>You will not hold PROP INDEXA responsible for any trading losses</li>
                <li style={{ marginBottom: '8px' }}>You are solely responsible for your trading decisions</li>
              </ul>
            </section>

            <section>
              <h2 style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>
                6. Seek Professional Advice
              </h2>
              <p>
                If you are unsure about any aspect of trading or the risks involved, we strongly recommend seeking independent financial advice before using our services. Trading is not suitable for everyone, and you should only trade if you fully understand and accept the risks involved.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskDisclosure;
